"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { BRAND } from "@/lib/media";
import { useRouter } from "next/router";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Globe, Mail, Phone } from "lucide-react";
const NAV_LINKS = [{
  label: "Girmitiya Proud",
  href: "/girmitiya-proud"
}, {
  label: "Countries",
  href: "/countries"
}, {
  label: "Councils",
  href: "#",
  children: [{
    label: "Governing Council",
    href: "/governing-council"
  }]
}, {
  label: "Reports",
  href: "/reports"
}, {
  label: "Publications",
  href: "/publications"
}, {
  label: "Events",
  href: "/events"
}, {
  label: "Archives",
  href: "/archives"
}, {
  label: "About",
  href: "/about"
}];
const LOGO_SRC = BRAND.logo;

// ── Top Bar ────────────────────────────────────────────────────────────────────
const TopBar = () => {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <a href="mailto:info@girmitiyacouncil.in" className="flex items-center gap-1.5 text-[12px] md:text-[13.5px] font-light tracking-wide text-white/70 hover:text-white transition-colors"><Mail size={11} strokeWidth={1.5} /><span className="hidden sm:inline">info@girmitiyacouncil.in</span><span className="sm:hidden">Email Us</span></a>;
    t1 = <span className="w-px h-3 bg-white/20" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex items-center gap-3 md:gap-4">{t0}{t1}<a href="tel:+919415949494" className="flex items-center gap-1.5 text-[12px] md:text-[13.5px] font-light tracking-wide text-white/70 hover:text-white transition-colors"><Phone size={11} strokeWidth={1.5} />+91-94159 49494</a></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="font-body bg-[#0F1C2E] h-[34px] flex items-center"><div className="w-full max-w-[1280px] mx-auto px-6 md:px-8 flex items-center justify-between">{t2}<div className="hidden sm:flex items-center gap-1.5 text-[12px] md:text-[13.5px] font-light italic text-white/60"><Globe size={11} strokeWidth={1.5} />Preserving Girmitiya Heritage Worldwide</div></div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};

// ── Desktop Dropdown ───────────────────────────────────────────────────────────
const DropdownMenu = t0 => {
  const $ = _c(5);
  const {
    items,
    isOpen
  } = t0;
  let t1;
  if ($[0] !== isOpen || $[1] !== items) {
    t1 = isOpen && <motion.div className="absolute top-[calc(100%+1px)] left-1/2 -translate-x-1/2 min-w-[210px] bg-[#F4EFE6] border border-[#0F1C2E]/15 border-t-2 border-t-[#DF6D2A] shadow-[0_8px_32px_rgba(26,20,16,0.10)] py-1.5 z-50" initial={{
      opacity: 0,
      y: 6,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 6,
      scale: 0.97
    }} transition={{
      duration: 0.16,
      ease: [0.4, 0, 0.2, 1]
    }}>{items.map(_temp)}</motion.div>;
    $[0] = isOpen;
    $[1] = items;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <AnimatePresence>{t1}</AnimatePresence>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};

// ── Mobile Drawer ──────────────────────────────────────────────────────────────
const MobileMenu = t0 => {
  const $ = _c(6);
  const {
    isOpen,
    onClose
  } = t0;
  const [openGroup, setOpenGroup] = useState(null);
  let t1;
  if ($[0] !== isOpen || $[1] !== onClose || $[2] !== openGroup) {
    t1 = isOpen && <><motion.div className="fixed inset-0 z-[1001] bg-[#0F1C2E]/45 backdrop-blur-sm" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} /><motion.nav className="fixed top-0 right-0 bottom-0 z-[1002] w-[min(360px,90vw)] bg-[#F4EFE6] flex flex-col overflow-y-auto" initial={{
        x: "100%"
      }} animate={{
        x: 0
      }} exit={{
        x: "100%"
      }} transition={{
        duration: 0.3,
        ease: [0.32, 0.72, 0, 1]
      }}><div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-[#0F1C2E]/10"><div className="flex items-center gap-3"><img src={LOGO_SRC} alt={BRAND.logoAlt} width={48} height={49} className="h-8 w-auto object-contain" decoding="async" /><div className="flex flex-col leading-none"><span className="font-display text-[18px] font-semibold text-[#0F1C2E] tracking-[-0.01em] leading-tight">Global Girmitiya Council</span><span className="font-body text-[9px] tracking-[0.12em] uppercase text-accent-readable font-light mt-0.5">Preserving the Indenture Legacy</span></div></div><button className="flex items-center justify-center w-10 h-10 border border-[#0F1C2E]/15 text-[#0F1C2E] hover:bg-[#E8DDD2] transition-colors" onClick={onClose} aria-label="Close menu"><X size={20} strokeWidth={1.5} /></button></div><div className="flex-1">{NAV_LINKS.map(link => link.children ? <div key={link.label}><button className="font-body flex items-center justify-between w-full px-6 py-[14px] text-[13px] tracking-[0.07em] uppercase text-[#4C596B] hover:text-[#0F1C2E] border-b border-[#0F1C2E]/10 transition-colors" onClick={() => setOpenGroup(openGroup === link.label ? null : link.label)}>{link.label}<motion.span animate={{
                rotate: openGroup === link.label ? 180 : 0
              }} transition={{
                duration: 0.2
              }}><ChevronDown size={15} strokeWidth={1.5} /></motion.span></button><AnimatePresence>{openGroup === link.label && <motion.div className="overflow-hidden bg-[#0F1C2E]/[0.02]" initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: "auto",
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.22
              }}>{link.children.map(child => <Link key={child.href} href={child.href} className="font-body block px-6 pl-9 py-[11px] text-[12px] tracking-[0.06em] uppercase text-[#4C596B] hover:text-[#DF6D2A] border-b border-[#0F1C2E]/10 transition-colors" onClick={onClose}>{child.label}</Link>)}</motion.div>}</AnimatePresence></div> : <Link key={link.href} href={link.href} className="font-body block px-6 py-[14px] text-[13px] tracking-[0.07em] uppercase text-[#4C596B] hover:text-[#0F1C2E] border-b border-[#0F1C2E]/10 transition-colors" onClick={onClose}>{link.label}</Link>)}</div><div className="p-6 border-t border-[#0F1C2E]/10 flex flex-col gap-4"><Link href="/contact" className="btn-primary font-body block text-center py-3.5 px-6 text-[13px] tracking-[0.1em] uppercase transition-colors" onClick={onClose}>Get in Touch</Link><div className="flex flex-col gap-1 text-center"><a href="mailto:info@girmitiyacouncil.in" className="font-body text-[12px] text-[#4C596B] hover:text-[#DF6D2A] transition-colors">info@girmitiyacouncil.in</a><a href="tel:+919415949494" className="font-body text-[12px] text-[#4C596B] hover:text-[#DF6D2A] transition-colors">+91-94159 49494</a></div></div></motion.nav></>;
    $[0] = isOpen;
    $[1] = onClose;
    $[2] = openGroup;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <AnimatePresence>{t1}</AnimatePresence>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
const isCountryDetailPath = pathname => /^\/countries\/[^/]+$/.test(pathname || "");

// ── Main Navbar ────────────────────────────────────────────────────────────────
const Navbar = () => {
  const $ = _c(50);
  const router = useRouter();
  let t0;
  if ($[0] !== router.pathname) {
    t0 = isCountryDetailPath(router.pathname);
    $[0] = router.pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isCountryDetailPage = t0;
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const closeTimer = useRef(null);
  let t1;
  let t2;
  if ($[2] !== isCountryDetailPage) {
    t1 = () => {
      const onScroll = () => {
        if (isCountryDetailPage) {
          setScrolled(false);
          return;
        }
        setScrolled(window.scrollY > 20);
      };
      onScroll();
      window.addEventListener("scroll", onScroll, {
        passive: true
      });
      return () => window.removeEventListener("scroll", onScroll);
    };
    t2 = [isCountryDetailPage];
    $[2] = isCountryDetailPage;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  const showTopBar = isCountryDetailPage || !scrolled;
  const spacerClass = isCountryDetailPage ? "h-[72px] lg:h-[106px]" : scrolled ? "h-[72px]" : "h-[72px] lg:h-[106px] transition-all duration-300";
  let t3;
  let t4;
  if ($[5] !== mobileOpen) {
    t3 = () => {
      document.body.style.overflow = mobileOpen ? "hidden" : "";
      return _temp2;
    };
    t4 = [mobileOpen];
    $[5] = mobileOpen;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t10;
  let t11;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[8] !== activeDropdown || $[9] !== isCountryDetailPage || $[10] !== scrolled || $[11] !== showTopBar) {
    const handleMouseEnter = label => {
      clearTimeout(closeTimer.current);
      setActiveDropdown(label);
    };
    let t12;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = () => {
        closeTimer.current = setTimeout(() => setActiveDropdown(null), 120);
      };
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    const handleMouseLeave = t12;
    t10 = `fixed top-0 left-0 w-full z-[1000] transition-shadow duration-300 ${scrolled ? "shadow-[0_1px_20px_rgba(26,20,16,0.10)]" : ""}`;
    const t13 = `hidden lg:block overflow-hidden ease-in-out ${isCountryDetailPage ? "" : "transition-all duration-300"} ${showTopBar ? "max-h-[34px] opacity-100" : "max-h-0 opacity-0"}`;
    let t14;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <TopBar />;
      $[20] = t14;
    } else {
      t14 = $[20];
    }
    if ($[21] !== t13) {
      t11 = <div className={t13}>{t14}</div>;
      $[21] = t13;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    t9 = "bg-[#F4EFE6]/95 backdrop-blur-md border-b border-[#0F1C2E]/10 h-[72px] flex items-center";
    t7 = "w-full max-w-[1280px] mx-auto px-6 md:px-8 flex items-center justify-between gap-6";
    let t15;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <img src={LOGO_SRC} alt={BRAND.logoAlt} width={48} height={49} className="h-10 sm:h-12 w-auto object-contain" decoding="async" />;
      $[23] = t15;
    } else {
      t15 = $[23];
    }
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <Link href="/" className="flex-shrink-0 flex items-center gap-2 sm:gap-3">{t15}<div className="hidden sm:flex flex-col leading-none"><span className="font-display text-[20px] font-semibold text-[#0F1C2E] tracking-[-0.01em] leading-tight">Global Girmitiya Council</span><span className="font-body text-[9.5px] tracking-[0.15em] uppercase text-accent-readable font-light mt-0.5">Preserving the Indenture Legacy</span></div></Link>;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    t5 = "hidden lg:flex items-center list-none m-0 p-0";
    t6 = NAV_LINKS.map(link => <li key={link.label} className="relative" onMouseEnter={() => link.children && handleMouseEnter(link.label)} onMouseLeave={handleMouseLeave}><Link href={link.href} className={`font-body flex items-center gap-1 px-3 xl:px-3.5 h-[72px] text-[14px] xl:text-[14px] tracking-[0.06em] uppercase border-b-2 transition-colors duration-150 whitespace-nowrap ${activeDropdown === link.label ? "text-[#0F1C2E] border-[#DF6D2A]" : "text-[#4C596B] border-transparent hover:text-[#0F1C2E] hover:border-[#DF6D2A]"}`}>{link.label}{link.children && <motion.span animate={{
          rotate: activeDropdown === link.label ? 180 : 0
        }} transition={{
          duration: 0.2
        }} className="opacity-60"><ChevronDown size={13} strokeWidth={1.5} /></motion.span>}</Link>{link.children && <DropdownMenu items={link.children} isOpen={activeDropdown === link.label} />}</li>);
    $[8] = activeDropdown;
    $[9] = isCountryDetailPage;
    $[10] = scrolled;
    $[11] = showTopBar;
    $[12] = t10;
    $[13] = t11;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t10 = $[12];
    t11 = $[13];
    t5 = $[14];
    t6 = $[15];
    t7 = $[16];
    t8 = $[17];
    t9 = $[18];
  }
  let t12;
  if ($[25] !== t5 || $[26] !== t6) {
    t12 = <ul className={t5}>{t6}</ul>;
    $[25] = t5;
    $[26] = t6;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Link href="/contact" className="btn-primary font-body hidden xl:flex items-center px-5 py-2.5 text-[12.5px] tracking-[0.08em] uppercase border border-accent-cta hover:bg-transparent hover:text-accent-cta transition-colors duration-200">Contact Us</Link>;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="flex items-center gap-3 flex-shrink-0">{t13}<button className="lg:hidden flex items-center justify-center w-10 h-10 border border-[#0F1C2E]/15 text-[#0F1C2E] hover:bg-[#E8DDD2] transition-colors" aria-label="Open menu" onClick={() => setMobileOpen(true)}><Menu size={20} strokeWidth={1.5} /></button></div>;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t12 || $[31] !== t7 || $[32] !== t8) {
    t15 = <div className={t7}>{t8}{t12}{t14}</div>;
    $[30] = t12;
    $[31] = t7;
    $[32] = t8;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t15 || $[35] !== t9) {
    t16 = <div className={t9}>{t15}</div>;
    $[34] = t15;
    $[35] = t9;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t10 || $[38] !== t11 || $[39] !== t16) {
    t17 = <header className={t10}>{t11}{t16}</header>;
    $[37] = t10;
    $[38] = t11;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setMobileOpen(false);
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== mobileOpen) {
    t19 = <MobileMenu isOpen={mobileOpen} onClose={t18} />;
    $[42] = mobileOpen;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== spacerClass) {
    t20 = <div className={spacerClass} aria-hidden="true" />;
    $[44] = spacerClass;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t17 || $[47] !== t19 || $[48] !== t20) {
    t21 = <>{t17}{t19}{t20}</>;
    $[46] = t17;
    $[47] = t19;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  return t21;
};
export default Navbar;
function _temp(item) {
  return <Link key={item.href} href={item.href} className="dropdown-item block px-4 py-2.5 text-[12.5px] tracking-[0.04em] uppercase text-[#4C596B] hover:text-[#DF6D2A] hover:bg-[#DF6D2A]/[0.04]">{item.label}</Link>;
}
function _temp2() {
  document.body.style.overflow = "";
}